import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Home } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const BestSellers = () => {
  const [data, setData] = useState(null);
  const [trending, setTrending] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/products/best-sellers", { params: { limit: 24 } }).then((r) => setData(r.data)),
      api
        .get("/products/trending", { params: { limit: 8 } })
        .then((r) => setTrending(r.data))
        .catch(() => setTrending(null)),
    ])
      .catch(() => setData({ source: "new_arrivals", products: [] }))
      .finally(() => setLoading(false));
  }, []);

  const isSales = data?.source === "sales";
  const products = data?.products || [];
  const hasTrending = trending?.source === "trending" && (trending.products || []).length > 0;

  return (
    <div data-testid="best-sellers-page" className="grain">
      {/* Header ribbon */}
      <section
        className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-10 border-b"
        style={{
          borderColor: "var(--rs-border)",
          background:
            "linear-gradient(180deg, rgba(30,15,10,0.55) 0%, rgba(10,10,10,0) 100%)",
        }}
      >
        <Link
          data-testid="best-sellers-back-link"
          to="/"
          className="inline-flex items-center gap-2.5 text-xs uppercase tracking-[0.28em] text-bone hover:opacity-90 transition mb-10 px-5 py-3 border"
          style={{
            borderColor: "var(--rs-terracotta, #c07050)",
            background: "rgba(192,112,80,0.08)",
          }}
        >
          <ArrowLeft strokeWidth={1.75} size={14} />
          <Home strokeWidth={1.75} size={14} />
          Return to the Villa
        </Link>

        <div className="max-w-5xl">
          <div className="overline mb-5">
            {isSales
              ? `House Favourites · Last ${data?.window_days || 30} Days`
              : "House Favourites"}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.05]">
            {isSales ? (
              <>
                What the{" "}
                <em className="not-italic" style={{ color: "var(--rs-terracotta)" }}>
                  house
                </em>{" "}
                is loving.
              </>
            ) : (
              <>
                Freshly{" "}
                <em className="not-italic" style={{ color: "var(--rs-terracotta)" }}>
                  stocked.
                </em>
              </>
            )}
          </h1>
          <p className="text-muted-bone mt-6 max-w-2xl text-base sm:text-lg leading-relaxed">
            {isSales
              ? "A rolling tally of what the atelier is quietly reordering. Ranked by real, recent orders — not opinion."
              : "The pieces the house has just laid out — a considered mix, drawn from every room. Once orders start flowing, this room reshuffles itself around the true favourites."}
          </p>
        </div>
      </section>

      {/* Trending — 7-day, time-decayed. Only appears when there's real momentum. */}
      {hasTrending && (
        <section
          data-testid="favourites-trending-section"
          className="px-6 md:px-12 lg:px-16 pt-14 md:pt-20 pb-6"
          style={{ background: "var(--rs-surface)" }}
        >
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div
                className="overline mb-3 flex items-center gap-2"
                style={{ color: "var(--rs-emerald, #7bc39b)" }}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--rs-emerald, #7bc39b)" }} />
                Trending · Last 7 Days
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-bone leading-tight">
                What&apos;s{" "}
                <em className="not-italic" style={{ color: "var(--rs-terracotta)" }}>
                  catching fire
                </em>{" "}
                this week.
              </h2>
              <p className="text-xs text-muted-bone mt-3 max-w-md leading-relaxed">
                Time-decayed score — a sale today counts more than one from six days ago.
                Momentum, not tenure.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {trending.products.slice(0, 8).map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        {loading ? (
          <div
            data-testid="best-sellers-loading"
            className="text-muted-bone text-sm tracking-[0.22em] uppercase"
          >
            Polishing the mantelpiece…
          </div>
        ) : products.length === 0 ? (
          <div className="text-muted-bone italic">
            The favourites shelf is still being arranged — return in a moment.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        )}

        <div className="mt-16 flex flex-col sm:flex-row justify-center gap-4">
          <Link
            data-testid="best-sellers-shop-all"
            to="/shop"
            className="px-10 py-4 uppercase text-xs tracking-[0.28em] text-bone border hover:bg-white/5 transition text-center"
            style={{ borderColor: "rgba(245,242,235,0.25)" }}
          >
            Browse the full villa →
          </Link>
          <Link
            data-testid="best-sellers-back-link-bottom"
            to="/"
            className="inline-flex items-center justify-center gap-2 px-10 py-4 uppercase text-xs tracking-[0.28em] text-bone hover:opacity-90 transition text-center"
            style={{ background: "var(--rs-oxblood)" }}
          >
            <Home strokeWidth={1.75} size={14} />
            Return to the Villa
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BestSellers;
